import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import axios from "axios";
import { useOAuthPopupConnect } from "../../hooks/oauthPopup";
import { DropdownSingle, colors2, UI_BODY_SM_DF } from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
  EmptyBanner,
} from "./BaseIntegrationInterface";
import { useFetchPreflightData } from "../../hooks/useFetchPreflightData";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// Airtable does NOT use the useOAuthConnection hook — its two-stage preflight
// (pick base → cascading fetchPreflight when selectedBase changes → pick table)
// doesn't match the single-preflight lifecycle the hook owns. We still adopt
// the shared banner styled components above for visual parity.

// ============================================
// AIRTABLE ICON
// ============================================
// Airtable brand mark — stylised three-colour grid. Approximation; replace
// with the official asset later if asset review wants exact pixels.

export const AirtableIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="6" fill="#FFFFFF" />
    <path d="M6 10l10-4 10 4-10 4z" fill="#FCB400" />
    <path d="M6 12v9l9 4v-9z" fill="#18BFFF" />
    <path d="M26 12v9l-9 4v-9z" fill="#F82B60" />
  </svg>
);

// ============================================
// STYLED COMPONENTS
// ============================================

const PickerRow = styled.div`
  margin-top: 16px;
`;

const HintLine = styled(UI_BODY_SM_DF)`
  color: ${colors2.grey60};
  margin-top: 8px;
`;

// ============================================
// AIRTABLE INTERFACE — OAuth + PKCE persistent connection
// ============================================

/**
 * Two-stage picker:
 *   Stage 1: list bases (preflight without baseId)
 *   Stage 2: list tables for the selected base (preflight with baseId)
 *
 * Both stages hit the same /api/externalImport/fetchPreflightData endpoint
 * — the adapter switches behaviour based on `options.baseId`.
 *
 * Three connection states (mirrors Slack/Typeform):
 *   1. Not connected → "Connect Airtable" CTA
 *   2. Connected     → base picker + table picker + "Import Records"
 *   3. Stale         → "Reconnect Airtable" banner
 */
export const AirtableInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
  submitError,
  onClearSubmitError,
}) => {
  // Show the empty-state info banner for both Airtable-specific and the
  // generic downstream "no data found" message (matches the Typeform polish).
  const submitErrorMessage =
    submitError && submitError.sourceId === "airtable" ? submitError.message : null;
  const isEmptyResult =
    submitErrorMessage &&
    /no\s+(airtable\s+)?(records|data)\s+(found|to)/i.test(submitErrorMessage);

  const [connection, setConnection] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [connectError, setConnectError] = useState(null);

  // Stage 1: bases
  const [bases, setBases] = useState(null);
  const [basesError, setBasesError] = useState(null);
  const [selectedBase, setSelectedBase] = useState(null);

  // Stage 2: tables of the selected base. Reset whenever base changes.
  const [tables, setTables] = useState(null);
  const [tablesError, setTablesError] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);

  const {
    mutate: fetchPreflight,
    isLoading: isPreflightLoading,
  } = useFetchPreflightData();

  // ---- Connection status ----

  const fetchConnectionStatus = useCallback(async () => {
    setConnectionLoading(true);
    try {
      const { data } = await axios.get("/api/externalConnections/airtable/status");
      setConnection(data);
    } catch (err) {
      setConnectError(err.response?.data?.error || err.message || "Failed to read Airtable connection status");
    } finally {
      setConnectionLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnectionStatus();
  }, [fetchConnectionStatus]);

  // Handle return from OAuth redirect — refetch status, surface any error.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("externalConnectionResult");
    const provider = params.get("provider");
    if (result && provider === "airtable") {
      if (result === "error") {
        const reason = params.get("reason") || "unknown";
        setConnectError(`Airtable connection failed: ${reason}. Please try again.`);
      }
      fetchConnectionStatus();
      params.delete("externalConnectionResult");
      params.delete("provider");
      params.delete("reason");
      const qs = params.toString();
      const newUrl = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, [fetchConnectionStatus]);

  // ---- Stage 1: load bases lazily once connection is known active ----

  useEffect(() => {
    if (!connection?.connected || connection?.status === "stale" || bases !== null) return;
    setBasesError(null);
    fetchPreflight(
      { sourceId: "airtable" }, // stage 1: no baseId in options
      {
        onSuccess: (data) => {
          setBases(data.preflightData?.bases || []);
        },
        onError: (err) => {
          setBasesError(err.message || "Failed to load Airtable bases.");
          // A 401 here will have marked the connection stale server-side;
          // refresh status so the UI flips to the Reconnect banner.
          fetchConnectionStatus();
        },
      }
    );
  }, [connection, bases, fetchPreflight, fetchConnectionStatus]);

  // ---- Stage 2: load tables when a base is picked ----

  useEffect(() => {
    if (!selectedBase?.value) {
      setTables(null);
      setSelectedTable(null);
      return;
    }
    setTables(null);
    setSelectedTable(null);
    setTablesError(null);
    fetchPreflight(
      {
        sourceId: "airtable",
        options: { baseId: selectedBase.value },
      },
      {
        onSuccess: (data) => {
          setTables(data.preflightData?.tables || []);
        },
        onError: (err) => {
          setTablesError(err.message || "Failed to load tables for the selected base.");
          fetchConnectionStatus();
        },
      }
    );
  }, [selectedBase, fetchPreflight, fetchConnectionStatus]);

  // ---- Connect / disconnect ----

  // Popup OAuth: the shared hook opens the popup, points it at the provider, and
  // calls onResult when the popup reports back (full-page redirect fallback is
  // handled by the URL-param effect above).
  const { startConnect, isConnecting } = useOAuthPopupConnect({
    providerId: "airtable",
    providerName: "Airtable",
    onResult: ({ result, reason }) => {
      if (result === "error") {
        setConnectError(`Airtable connection failed: ${reason || "unknown"}. Please try again.`);
      }
      fetchConnectionStatus();
    },
  });

  const handleConnect = useCallback(async () => {
    setConnectError(null);
    try {
      await startConnect();
    } catch (err) {
      setConnectError(err.response?.data?.error || err.message || "Could not start Airtable connection");
    }
  }, [startConnect]);

  const handleDisconnect = useCallback(async () => {
    setConnectError(null);
    try {
      await axios.post("/api/externalConnections/airtable/disconnect");
      setConnection({ connected: false });
      setBases(null);
      setSelectedBase(null);
      setTables(null);
      setSelectedTable(null);
    } catch (err) {
      setConnectError(err.response?.data?.error || err.message || "Disconnect failed");
    }
  }, []);

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await handleDisconnect();
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  // ---- Submit ----

  const isSelectionComplete = Boolean(selectedBase?.value && selectedTable?.value);

  const handleSubmit = () => {
    if (!isSelectionComplete) return;
    onClearSubmitError && onClearSubmitError();
    onSubmit({
      adapterOptions: {
        baseId: selectedBase.value,
        tableId: selectedTable.value,
      },
    });
  };

  // ---- Render ----

  if (connectionLoading) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Airtable</InterfaceHeader>
        <InterfaceDescription>Checking connection status…</InterfaceDescription>
      </InterfaceContainer>
    );
  }

  // State 1: not connected
  if (!connection?.connected) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Airtable</InterfaceHeader>
        <InterfaceDescription>
          Import records from an Airtable base. You'll be redirected to
          Airtable to authorize access, then brought back here to pick a base
          and table.
        </InterfaceDescription>

        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}

        <InterfaceActions>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Connect Airtable</ButtonPrimary>
        </InterfaceActions>

        <HelpText>
          Dots uses OAuth + PKCE to access your Airtable account. We request
          read access to bases, schema, and your email — your team never sees
          a raw API token.
        </HelpText>
      </InterfaceContainer>
    );
  }

  // State 3: stale connection
  if (connection.status === "stale") {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Reconnect Airtable</InterfaceHeader>
        <StaleBanner>
          <ConnectedMeta>
            <strong>⚠ Connection needs to be renewed</strong>
            <span>
              {connection.providerAccountName
                ? `Your Airtable connection for ${connection.providerAccountName} expired or was revoked.`
                : "Your Airtable connection expired or was revoked."}
            </span>
          </ConnectedMeta>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Reconnect Airtable</ButtonPrimary>
        </StaleBanner>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      </InterfaceContainer>
    );
  }

  // State 2: connected & ready to import
  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Airtable</InterfaceHeader>
      <InterfaceDescription>
        Each Airtable record becomes one row. Field names map to columns;
        attachments are stored as their Airtable URLs.
      </InterfaceDescription>

      <ConnectedBanner>
        <ConnectedMeta>
          <strong>
            ✓ Connected{connection.adminEmail ? ` as ${connection.adminEmail}` : ""}
          </strong>
          {connection.authorizedBy?.name && (
            <span style={{ color: colors2.grey60 }}>
              Authorized by: {connection.authorizedBy.name}
            </span>
          )}
        </ConnectedMeta>
        <ButtonSecondary onClick={() => setShowDisconnectConfirm(true)} disabled={isLoading}>
          Disconnect
        </ButtonSecondary>
      </ConnectedBanner>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name="Airtable"
          onConfirm={confirmDisconnect}
          onClose={() => setShowDisconnectConfirm(false)}
          isLoading={isDisconnecting}
        />
      )}

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      {basesError && <ErrorBanner>{basesError}</ErrorBanner>}
      {tablesError && <ErrorBanner>{tablesError}</ErrorBanner>}
      {submitErrorMessage && isEmptyResult && (
        <EmptyBanner>{submitErrorMessage}</EmptyBanner>
      )}
      {submitErrorMessage && !isEmptyResult && (
        <ErrorBanner>{submitErrorMessage}</ErrorBanner>
      )}

      <PickerRow>
        <DropdownSingle
          label="Select Base"
          placeholder={
            isPreflightLoading && bases === null
              ? "Loading bases..."
              : "Choose a base..."
          }
          options={(bases || []).map((b) => ({
            display: b.name || b.id,
            value: b.id,
            _baseInfo: b,
          }))}
          value={selectedBase}
          onChange={(k, v) => setSelectedBase(v)}
          readOnly={isPreflightLoading || isLoading || bases === null}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </PickerRow>

      <PickerRow>
        <DropdownSingle
          label={adapterConfig?.preflightConfig?.selectLabel || "Select Table"}
          placeholder={
            !selectedBase
              ? "Pick a base first"
              : isPreflightLoading && tables === null
                ? "Loading tables..."
                : adapterConfig?.preflightConfig?.selectPlaceholder || "Choose a table to import..."
          }
          options={(tables || []).map((t) => ({
            display: t.name || t.id,
            value: t.id,
            _tableInfo: t,
          }))}
          value={selectedTable}
          onChange={(k, v) => setSelectedTable(v)}
          readOnly={!selectedBase || isPreflightLoading || isLoading || tables === null}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </PickerRow>

      <HintLine>
        Heads up: Airtable attachment URLs expire about 2 hours after import.
        Use them for preview only — long-term references should be saved
        elsewhere.
      </HintLine>

      <InterfaceActions>
        <ButtonPrimary
          onClick={handleSubmit}
          disabled={!isSelectionComplete || isPreflightLoading || isLoading}
          isLoading={isLoading}
        >
          {adapterConfig?.preflightConfig?.submitLabel || "Import Records"}
        </ButtonPrimary>
      </InterfaceActions>
    </InterfaceContainer>
  );
};

// ============================================
// INTEGRATION META
// ============================================

export const airtableIntegrationMeta = {
  id: "airtable",
  name: "Airtable",
  description: "Import records from an Airtable base and table",
};

export default AirtableInterface;
